import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCategories,
  fetchIkpuCodes,
  fetchIkpuNames,
  fetchRegions,
  resolveNiche,
  type ProcurementKind,
} from '../lib/catalog-client';
import { searchBuyers, type BuyerHit } from '../lib/buyers-client';
import { useLocale } from '../i18n/locale-context';
import { countryConfig } from '../country';
import {
  previewSavedSearchCount,
  savedSearchToCatalogPath,
  updateSavedSearch,
  type SavedSearch,
} from '../lib/saved-search-client';
import { SAVED_SEARCH_TERMS_MAX } from '../lib/saved-search-limits';
import { ApiError, NetworkError } from '../lib/api';
import { useLocaleNavigate } from '../i18n/locale-link';
import { stripLocalePrefix } from '../i18n/locale-path';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from './ui/sheet';
import { SubjectPicker, type SubjectSelection } from './ui/subject-picker';
import { TagInput } from './ui/tag-input';
import { ChipStrip } from './ui/chip-strip';
import { Checkbox } from './ui/checkbox';
import { BudgetInput, BUDGET_CEILING_PICKS, BUDGET_FLOOR_PICKS } from './ui/budget-input';
import { useVisualViewportSheetHeight } from '../lib/use-visual-viewport-sheet-height';
import { regionChipLabel } from '../i18n/region-chip-label';
import { useAiOnboarding } from '../lib/use-ai-onboarding';
import { AiFillBlock } from './ai-fill-block';
import { AiSparkle } from './ai-search-cards';
import { countDraftEdits, type AiDraft } from '../lib/ai-proposal-draft';
import { reportOutcomeQuietly, type AiProposeResponse } from '../lib/ai-search-client';

const PROCUREMENT_KINDS = ['goods', 'works', 'services'] as const;

/** Spain: a search with codes matches by its codes; its words do not narrow it
 *  (`features.codesOverTerms`, the API's `narrowingTextTerms`). */
const CODES_OVER_TERMS = countryConfig.features.codesOverTerms === true;

/** Spain: a code chip is named from the FULL code table (`fetchIkpuNames`), not
 *  only from the browse list, which keeps a deep code only with 5+ lots. */
function codeNamesFromTable(): boolean {
  return countryConfig.features.codeNamesFromTable === true;
}

type TFn = (key: string, params?: Record<string, string | number>) => string;

/**
 * What to tell the person when a save is refused — in THEIR language.
 *
 * This line used to print `error.message` as is. Every refusal the API can
 * produce here is written in English for a developer, so a subscriber whose
 * search held more words than the validator allowed read «textTerms must
 * contain no more than 10 elements» under the Save button (reported from
 * production 2026-10-01). The server's wording is never shown now: a known
 * code maps to a sentence that says what to change, anything else to «не
 * получилось, попробуйте ещё раз».
 *
 * A plain `Error` is the sheet's OWN message (`saved.budgetRangeError`, thrown
 * by the mutation guard below) — already translated, so it passes through.
 */
function saveErrorText(error: unknown, t: TFn): string {
  if (error instanceof ApiError) {
    if (error.code === 'empty_criteria') return t('saved.emptyCriteria');
    if (error.code === 'subject_axis_required') return t('saved.edit.errorSubject');
    if (error.code === 'budget_ceiling_below_floor') return t('saved.budgetRangeError');
    if (error.code === 'validation_error') {
      const field = error.details?.['field'];
      return field === 'textTerms' || field === 'excludeTerms'
        ? t('saved.edit.errorTerms')
        : t('saved.edit.errorInvalid');
    }
    return t('saved.errorGeneric');
  }
  // A `NetworkError`'s message is a developer string too («network request
  // failed»), so it gets the generic sentence, not a pass-through.
  if (error instanceof Error && !(error instanceof NetworkError) && error.message) {
    return error.message;
  }
  return t('saved.errorGeneric');
}

/**
 * Is the page BEHIND this sheet the catalog list?
 *
 * Only the `/catalog` tree, deliberately — `/ifi` and `/supply` are rendered by
 * the same component but are different каталожные слои, and
 * `savedSearchToCatalogPath` emits no scope, so "refreshing" an `/ifi` browser
 * with it would quietly move them into the gov+corporate layer. The pagination
 * and facet routes (`/catalog/page/3`, `/catalog/category/x`) count: they are the
 * same list, and the fresh criteria URL legitimately resets the page.
 */
export function isCatalogListPath(pathname: string): boolean {
  const bare = stripLocalePrefix(pathname);
  return bare === '/catalog' || bare.startsWith('/catalog/');
}

/**
 * Edit an existing saved search's refinements: name, budget range (floor +
 * ceiling), exclude term.
 *
 * A bottom sheet rather than an inline popover because 99% of traffic is mobile
 * and the host is a narrow sidebar row — an inline editor there would be a
 * cramped, easily-mis-tapped target on a phone.
 *
 * Refinements are editable, plus the WHOLE subject axis — category, ИКПУ and
 * niche alike, all three at once via the shared `SubjectPicker`.
 *
 * That is a deliberate softening of the rule this component shipped with
 * ("changing the subject makes it a different subscription"), and the
 * distinction is add-versus-replace. Swapping construction for office-supplies
 * would indeed be a different query; adding office-supplies — or an ИКПУ code,
 * or a niche — alongside it is the same person saying "also this". The free
 * tier allows exactly ONE saved search, so without this a free user who bids
 * across several categories, codes or niches cannot express it at all.
 *
 * `compileCriteria` unions category/ikpu/niche into ONE subject axis server-
 * side, so this sheet mirrors that: the three chip groups below share a single
 * "at least one across all three" removal guard (`subjectCount`), not three
 * independent ones. The catalog layer (`scope`) stays fixed — the API
 * enforces that one.
 *
 * The category axis needed nothing downstream changing: its slugs are stored
 * comma-joined in the legacy `categorySlug`, which the catalog's `category`
 * param, `buildWhere` and `criteriaFromLegacy` all already read as a union, so
 * the digest deep link stays EXACT for a multi-category search. ИКПУ and niche
 * do NOT have that property — `SavedSearch.ikpuCode`/`.nicheSlug` are single
 * columns, so only the FIRST of each mirrors there; the full lists live only in
 * `criteria`, same as the API side (`saved-search.repository.ts`).
 */
export function SavedSearchEditSheet({
  search,
  open,
  onOpenChange,
  aiFill = true,
}: {
  search: SavedSearch | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Offer «Заполнить с ИИ» at the top (where `features.aiOnboarding` is on).
   *
   * False for exactly one caller: the onboarding «Изменить», which opens this
   * sheet on a search a proposal just produced. Asking that person to describe
   * what they need is asking them the question they answered thirty seconds
   * ago, and it would spend a second attempt from a two-attempt allowance.
   */
  aiFill?: boolean;
}) {
  const { t, locale } = useLocale();
  const qc = useQueryClient();
  // Where the edit lands once it is saved — see `save.onSuccess`. Locale-aware
  // (`/uz/catalog…` for an Uzbek reader) like every other in-app navigation.
  const navigate = useLocaleNavigate();
  const { pathname } = useLocation();

  const [name, setName] = useState('');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [excludeTerms, setExcludeTerms] = useState<string[]>([]);
  // Terms the user agreed to in the bot after a 👎. Shown here because that
  // confirmation promises they can be undone on the site — and because a filter
  // you cannot see is how a subscription goes quiet for no visible reason.
  const [learned, setLearned] = useState<string[]>([]);
  // `learned` as SEEDED, so `save` can tell "the user removed a chip" apart
  // from "this field simply exists" — see `learnedChanged` and the
  // mutationFn below for why that distinction is the whole fix.
  const [seededLearned, setSeededLearned] = useState<string[]>([]);
  // The subject axis this subscription covers — category, ИКПУ and niche, all
  // three editable here (see the note on the component).
  const [categories, setCategories] = useState<string[]>([]);
  const [ikpuCodes, setIkpuCodes] = useState<string[]>([]);
  const [nicheSlugs, setNicheSlugs] = useState<string[]>([]);
  // Display names for the seeded ИКПУ codes / niche slugs, keyed by code/slug.
  // `criteria.include.ikpu`/`.niche` carry only the raw values — a chip needs
  // a NAME, and unlike categories (resolved via `fetchCategories` below) there
  // is no single "fetch every ИКПУ/niche" call cheap enough to always run, so
  // niche names are resolved on demand (one `resolveNiche` per slug) and ИКПУ
  // names fall back to the same facet list `SubjectPicker` itself browses.
  const [nicheNames, setNicheNames] = useState<Record<string, string>>({});
  const resolvedNicheSlugs = useRef<Set<string>>(new Set());
  // The three independent axes (ANDed against the subject and each other):
  // region, procurement kind, free text. All three were already stored and
  // compiled by the matcher/catalog/digest-link — nothing in this sheet could
  // set them until now.
  const [regions, setRegions] = useState<string[]>([]);
  const [kinds, setKinds] = useState<ProcurementKind[]>([]);
  const [textTerms, setTextTerms] = useState<string[]>([]);
  // Opt in/out of also matching `supply`-scope (cooperation.uz) leads — the
  // ONE lever on this sheet independent of `scope` itself (see
  // `scopesMatchingSavedSearch`). `SaveSearchButton` sets this at CREATE time
  // for a search born on `/supply`, but this is the only place it can be
  // changed afterwards, either direction.
  const [includeSupply, setIncludeSupply] = useState(false);
  // Pinned buyer (заказчик) — a fourth independent axis alongside region/
  // kind/text, same "ANDed against the subject" placement. Unlike those three
  // this is a SINGLE pin, not a list: `SavedSearch.customerTin` is one column
  // server-side (mirrors `Tender.customerTin`), not a many-valued criteria
  // axis, so there is no add-another-buyer chip strip here.
  const [customerTin, setCustomerTin] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  // The autocomplete's own local state — separate from `customerName`, which
  // only changes on a PICK. Typing must not overwrite the pinned buyer's name
  // until the user actually chooses a new hit (or clears the field).
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState<BuyerHit[]>([]);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const customerAbortRef = useRef<AbortController | null>(null);
  const customerFieldRef = useRef<HTMLDivElement | null>(null);

  // ── ИИ-заполнение ────────────────────────────────────────────────────────
  // Behind the same country switch as the onboarding step
  // (`features.aiOnboarding`: Kazakhstan on, Uzbekistan off). Off, this whole
  // block is absent and the builder is byte-for-byte what it was.
  const aiOn = useAiOnboarding();
  const [aiDraftId, setAiDraftId] = useState<string | null>(null);
  /** Which labels wear the ✦. A field the model left alone must not claim it. */
  const [aiFields, setAiFields] = useState<Set<string>>(new Set());
  /**
   * The form as the fill LEFT it — the baseline `editsCount` measures from.
   *
   * Not the proposal and not the pre-fill form, and both alternatives are
   * wrong in the same direction. This sheet edits an EXISTING search, so the
   * proposal does not mention the axes it left alone (exclusions, a buyer pin,
   * the kind filter) while the form still carries them; measuring against
   * either would report the subscriber's own saved search back to us as
   * corrections the model needed. The question the harness is asking is «что
   * человек поправил ПОСЛЕ того, как модель ответила», so the answer has to
   * start counting from the moment the answer landed.
   */
  const [aiBaseline, setAiBaseline] = useState<AiDraft | null>(null);
  /** ИКПУ names straight from the proposal — the facet list only covers codes
   *  with live lots, and a proposed code can legitimately have none today. */
  const [aiIkpuNames, setAiIkpuNames] = useState<Record<string, string>>({});
  /** Suggested exclusions the user has not accepted. Offered under the field
   *  as «+» chips, never applied silently — same rule as the onboarding step. */
  const [aiExcludeOffers, setAiExcludeOffers] = useState<string[]>([]);

  // `search` is read through a ref inside the seeding effect rather than as
  // an effect dep, kept current on every render (a plain render-body
  // assignment, not a second effect, so there's no lag between a new prop
  // landing and the ref reflecting it). The effect itself only fires on
  // `[search?.id, open]` — see below for why identity and even id-driven
  // re-seeding-while-open are both wrong here.
  const searchRef = useRef(search);
  searchRef.current = search;

  // Re-seed on every OPEN — not just when a different `search` object arrives
  // — and key on `search?.id`, not `search` itself.
  //
  // ⚠️ A BACKGROUND REFETCH RE-SEEDING OVER IN-SHEET EDITS. The query client
  //    has `refetchOnWindowFocus: true` + `staleTime: 30_000`
  //    (`src/lib/query-client.ts`), and every caller of this sheet (both
  //    `SaveSearchButton` and the sidebar row) reads its target off the same
  //    shared `['saved-searches']` cache, pinned into local state at open/
  //    click time rather than re-derived on every render. A paid user opens
  //    this sheet, edits regions, switches apps, comes back: focus triggers a
  //    refetch. TanStack Query's default `structuralSharing: true`
  //    means a refetch that returns BYTE-IDENTICAL rows keeps the same object
  //    reference (so `search` itself doesn't even change on that path) — the
  //    refetch that matters is one where the row genuinely changed (e.g. the
  //    Telegram bot writing `criteria.exclude.learned` after a 👎, or another
  //    browser tab editing the same alert), which DOES get a new reference
  //    with the SAME `id`. Keying on `search` (object identity) re-ran the
  //    seeding effect on that refetch and silently reverted every edit the
  //    user had just made. Keying on `search?.id` instead means a refetch for
  //    the same alert — identical OR genuinely changed — cannot re-seed;
  //    only a different alert (different id) or a fresh open can.
  //
  //    ⚠️ TRADE-OFF, not a free fix: because the effect does not re-run when
  //    only the id stays the same, a genuine background write to a field this
  //    sheet doesn't itself edit — `learnedExclusions` is the concrete case —
  //    stays at whatever it was seeded to. If the Telegram 👎 flow adds a
  //    learned exclusion WHILE this sheet is open, saving from the open sheet
  //    sends the stale (pre-👎) `learnedExclusions` and silently erases the
  //    one the bot just added. This is the right DEFAULT for an open form —
  //    the user's in-progress edits must beat a background write, not the
  //    other way around — but it is a real, known cost of this design, not
  //    an oversight; a future fix would need to merge the background write
  //    into `learned` specifically rather than re-seeding wholesale.
  //
  //    ⚠️ UPDATE: the "silently erases" half of that is now fixed, but not
  //    here — `learnedExclusions` is sent to the API only when THIS sheet
  //    itself removed a chip (see `learnedChanged` near the `save` mutation
  //    below), so an omitted field lets the repository's own merge guard
  //    keep whatever the bot just wrote. The re-seeding trade-off described
  //    above still holds — `learned` in this form still doesn't visibly pick
  //    up the bot's write until the sheet is reopened — it just no longer
  //    overwrites the STORED value on save.
  //
  // Do not "simplify" this back to depending on the `search` object itself —
  // that is exactly the bug above.
  useEffect(() => {
    if (!open || !searchRef.current) return;
    const current = searchRef.current;
    setName(current.name);
    setMinBudget(current.minBudgetUzs ? String(Number(current.minBudgetUzs)) : '');
    setMaxBudget(current.maxBudgetUzs ? String(Number(current.maxBudgetUzs)) : '');
    // Prefer the blob: it is authoritative and can hold more than one term.
    // The scalar is the legacy spelling and holds only the first.
    setExcludeTerms(
      current.criteria?.exclude?.text ?? (current.excludeKeyword ? [current.excludeKeyword] : []),
    );
    const baseLearned = current.criteria?.exclude?.learned ?? [];
    setLearned(baseLearned);
    setSeededLearned(baseLearned);
    // Stored comma-joined in the legacy scalar; the whole stack already reads it
    // that way (catalog `category` param, buildWhere, criteriaFromLegacy).
    const baseCategories = current.categorySlug
      ? current.categorySlug.split(',').filter(Boolean)
      : [];
    // Same reasoning as excludeTerms/categories above: fall back to the legacy
    // scalar for a row with no blob. Without this, opening a keyword- or
    // region-only search (no criteria yet) seeded an EMPTY axis, and saving
    // with no changes sent `[]` — which the API correctly reads as "clear this
    // axis" — silently deleting the filter that made the search match anything
    // in the first place.
    const baseRegions =
      current.criteria?.include?.region ?? (current.regionCode ? [current.regionCode] : []);
    // `kind` has no legacy scalar to fall back to — it is a new axis, never
    // stored anywhere before the criteria blob existed.
    const baseKinds = current.criteria?.include?.kind ?? [];
    const baseTextTerms =
      current.criteria?.include?.text ?? (current.keyword ? [current.keyword] : []);
    // Same legacy-fallback reasoning as categories/regions/text above —
    // `ikpuCode`/`nicheSlug` are the pre-blob single-value spelling.
    const baseIkpuCodes =
      current.criteria?.include?.ikpu ?? (current.ikpuCode ? [current.ikpuCode] : []);
    const baseNicheSlugs =
      current.criteria?.include?.niche ?? (current.nicheSlug ? [current.nicheSlug] : []);

    setCategories(baseCategories);
    setIkpuCodes(baseIkpuCodes);
    setNicheSlugs(baseNicheSlugs);
    // A fresh `search` may reuse names this component already resolved for a
    // DIFFERENT alert's slugs — reset so a stale name never flashes before the
    // resolve effect below catches up.
    setNicheNames({});
    resolvedNicheSlugs.current = new Set();
    setRegions(baseRegions);
    setKinds(baseKinds);
    setTextTerms(baseTextTerms);
    setIncludeSupply(current.includeSupply ?? false);
    // Same legacy-fallback shape as region/text above: `criteria.include.
    // customerTin` is authoritative when present, `customerTin` the scalar
    // mirror for a row saved (or never touched) before this axis grew a
    // criteria entry. The display name has no criteria-blob home of its own —
    // it always comes from the scalar `customerName` cache, which the API
    // keeps in step with `customerTin` on every write.
    const baseCustomerTin = current.criteria?.include?.customerTin?.[0] ?? current.customerTin;
    setCustomerTin(baseCustomerTin ?? null);
    setCustomerName(baseCustomerTin ? current.customerName : null);
    setCustomerQuery('');
    setCustomerResults([]);
    setCustomerDropdownOpen(false);
    customerAbortRef.current?.abort();
    customerAbortRef.current = null;
    // A fill belongs to ONE opening of ONE search. Carrying the ✦ marks or the
    // draft id across would attribute this search's fields to a proposal made
    // for a different one.
    setAiDraftId(null);
    setAiFields(new Set());
    setAiBaseline(null);
    setAiIkpuNames({});
    setAiExcludeOffers([]);
    // Deliberately `[search?.id, open]` — NOT `[search, open]`. See the block
    // comment above.
    //
    // ⚠️ NO TEST NET on this dep list from either call site: `SaveSearchButton`
    // pins `search={tuneTarget}` and the sidebar row pins its own `editing`
    // state the same way (both set only on open/click), so a background
    // refetch never changes this prop's identity from either — reverting to
    // `[search, open]` passes every test in this file. The identity-vs-id
    // distinction is real for a caller that passes a live query object
    // directly, but nothing here currently exercises that path.
  }, [search?.id, open]);

  // Buyer (заказчик) type-ahead: debounced 300ms, aborting the previous
  // in-flight request before a new one starts — mirrors `SubjectPicker`'s own
  // ИКПУ/niche debounce (200ms there), just with an explicit `AbortController`
  // since `searchBuyers` takes one and this field isn't routed through
  // TanStack Query (which would dedupe/cancel by query key instead).
  useEffect(() => {
    if (!open) return;
    const term = customerQuery.trim();
    if (term.length < 2) {
      setCustomerResults([]);
      setCustomerDropdownOpen(false);
      return;
    }
    const id = setTimeout(() => {
      customerAbortRef.current?.abort();
      const controller = new AbortController();
      customerAbortRef.current = controller;
      searchBuyers(term, controller.signal).then((hits) => {
        // A newer keystroke may have started a later request while this one
        // was in flight — `searchBuyers` swallows the AbortError and resolves
        // `[]` rather than rejecting, so an identity check (not `.aborted`)
        // is what actually stops a slow, stale response from overwriting a
        // faster, newer one.
        if (customerAbortRef.current !== controller) return;
        setCustomerResults(hits);
        setCustomerDropdownOpen(hits.length > 0);
      });
    }, 300);
    return () => clearTimeout(id);
  }, [customerQuery, open]);

  // Close the buyer type-ahead on an outside click. Without this it only closes
  // on a pick or when the query drops below 2 chars, so a stale results list
  // lingers over the rest of the sheet. Options live inside `customerFieldRef`,
  // so a pointerdown on one is `contains` → true and the pick still fires.
  useEffect(() => {
    if (!customerDropdownOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (!customerFieldRef.current?.contains(e.target as Node)) {
        setCustomerDropdownOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [customerDropdownOpen]);

  function pickCustomer(hit: BuyerHit) {
    setCustomerTin(hit.tin);
    setCustomerName(hit.name);
    setCustomerQuery('');
    setCustomerResults([]);
    setCustomerDropdownOpen(false);
  }
  function clearCustomer() {
    setCustomerTin(null);
    setCustomerName(null);
    setCustomerQuery('');
    setCustomerResults([]);
    setCustomerDropdownOpen(false);
  }

  /** The form's current state in the shape the edit counter compares. */
  function currentAiDraft(): AiDraft {
    return {
      name,
      categories: categories.map((slug) => ({ slug, name: slug })),
      ikpu: ikpuCodes.map((code) => ({ code, name: code })),
      niches: nicheSlugs.map((slug) => ({ slug, name: slug })),
      keywords: textTerms,
      regions: regions.map((code) => ({ code, name: code })),
      excludes: excludeTerms,
      minBudgetUzs: minBudget.trim() === '' ? null : Number(minBudget),
    };
  }

  /**
   * Put a proposal into the form.
   *
   * REPLACES the axes the model spoke about and leaves every other field
   * alone — «ИИ заполнит поля», not «ИИ перепишет поиск». An axis the model
   * returned nothing for keeps whatever the search already had, and gets no
   * ✦: the mark means "a model put this here", so putting it on a value the
   * user wrote themselves would be a small lie in the one place the feature
   * asks to be trusted.
   */
  function applyAiProposal(res: AiProposeResponse) {
    const p = res.proposal;
    const marks = new Set<string>();

    const nextName = p.name.trim() || name;
    if (p.name.trim()) marks.add('name');

    // A grouped facet's slug IS the comma union («it-telecom,telecom-equipment»)
    // and this form stores the MEMBERS — see `groupedCategorySelections`.
    const proposedCats = p.categories.flatMap((c) => c.slug.split(',').filter(Boolean));
    const spokeAboutSubject = proposedCats.length > 0 || p.ikpu.length > 0 || p.niches.length > 0;
    const nextCats = spokeAboutSubject ? proposedCats : categories;
    const nextIkpu = spokeAboutSubject ? p.ikpu.map((i) => i.code) : ikpuCodes;
    const nextNiches = spokeAboutSubject ? p.niches.map((n) => n.slug) : nicheSlugs;
    if (spokeAboutSubject) marks.add('subject');

    const nextText = p.keywords.length > 0 ? p.keywords : textTerms;
    if (p.keywords.length > 0) marks.add('text');

    const nextRegions = p.regions.length > 0 ? p.regions.map((r) => r.code) : regions;
    if (p.regions.length > 0) marks.add('regions');

    const nextMinBudget = p.minBudgetUzs != null ? String(p.minBudgetUzs) : minBudget;
    if (p.minBudgetUzs != null) marks.add('budget');

    setName(nextName);
    setCategories(nextCats);
    setIkpuCodes(nextIkpu);
    setNicheSlugs(nextNiches);
    setTextTerms(nextText);
    setRegions(nextRegions);
    setMinBudget(nextMinBudget);
    setAiIkpuNames(Object.fromEntries(p.ikpu.map((i) => [i.code, i.name])));
    // Names arrived with the proposal, so the per-slug `resolveNiche` lookups
    // have nothing left to fetch — mark them resolved before the effect runs.
    setNicheNames((prev) => ({
      ...prev,
      ...Object.fromEntries(p.niches.map((n) => [n.slug, n.name])),
    }));
    for (const n of p.niches) resolvedNicheSlugs.current.add(n.slug);

    setAiExcludeOffers(p.suggestedExcludes.filter((x) => !excludeTerms.includes(x)));
    setAiFields(marks);
    setAiDraftId(res.draftId);
    // The baseline is the form as this fill leaves it — see `aiBaseline`.
    setAiBaseline({
      name: nextName,
      categories: nextCats.map((slug) => ({ slug, name: slug })),
      ikpu: nextIkpu.map((code) => ({ code, name: code })),
      niches: nextNiches.map((slug) => ({ slug, name: slug })),
      keywords: nextText,
      regions: nextRegions.map((code) => ({ code, name: code })),
      excludes: excludeTerms,
      minBudgetUzs: nextMinBudget.trim() === '' ? null : Number(nextMinBudget),
    });
  }

  // Whether the user actually removed a learned (👎) chip since the sheet
  // opened — the only edit this sheet can make to `learned` (there is no "add
  // learned term" control, only the ✕ on an existing chip).
  //
  // ⚠️ Load-bearing for why `learnedExclusions` is sent CONDITIONALLY below.
  // `saved-search.repository.ts`'s patch handler treats a PRESENT
  // `learnedExclusions` field as authoritative and replaces the stored list
  // wholesale; its `?? next.exclude?.learned` fallback (keep what's already
  // stored) only fires when the field is OMITTED. The seeding effect above
  // deliberately does NOT re-seed `learned` on a background refetch for the
  // same alert id (see its block comment) — so if the Telegram 👎 flow adds a
  // learned exclusion while this sheet sits open, `learned` here is stale by
  // design. Sending it unconditionally on save would silently overwrite that
  // fresh exclusion with the stale pre-open list. Sending it only when this
  // sheet itself changed it lets the repository's merge guard do its job in
  // every other case — a rename, a budget edit, anything that isn't a 👎 chip
  // removal — without this sheet needing to know or merge the bot's write.
  const learnedChanged =
    learned.length !== seededLearned.length || learned.some((t) => !seededLearned.includes(t));

  // A ceiling below the floor can never match anything — the API rejects it
  // (400), but catching it here means the button disables and the reason
  // shows BEFORE a round trip, not after one fails.
  const budgetRangeInvalid =
    minBudget.trim() !== '' && maxBudget.trim() !== '' && Number(maxBudget) < Number(minBudget);

  const subjectCount = categories.length + ikpuCodes.length + nicheSlugs.length;

  /**
   * How many live tenders the CURRENT form state matches, shown on the Save
   * button.
   *
   * Debounced 400ms: seven axes feed it and the exclude/text tag inputs would
   * otherwise refetch on every keystroke. The button keeps the LAST known
   * number while a new one is in flight rather than flashing a spinner —
   * a figure that blinks on every character is worse than one that lags.
   */
  const previewInput = useMemo(
    () => ({
      categorySlugs: categories,
      ikpuCodes,
      nicheSlugs,
      regions,
      kinds,
      textTerms,
      excludeTerms,
      customerTin: customerTin ?? undefined,
      minBudgetUzs: minBudget.trim() === '' ? undefined : Number(minBudget),
      maxBudgetUzs: maxBudget.trim() === '' ? undefined : Number(maxBudget),
      // Not an editable axis — sent because the count is computed with it.
      // Without it an ifi/corporate subscription was counted against the gov
      // catalogue and the button promised lots it would never deliver.
      scope: search?.scope,
      // Unlike `scope`, THIS one the sheet DOES edit (the checkbox below) —
      // included so toggling it moves the live count, not just the saved row.
      includeSupply,
    }),
    [
      search?.scope,
      categories,
      ikpuCodes,
      nicheSlugs,
      regions,
      kinds,
      textTerms,
      excludeTerms,
      customerTin,
      minBudget,
      maxBudget,
      includeSupply,
    ],
  );
  const [debouncedInput, setDebouncedInput] = useState(previewInput);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedInput(previewInput), 400);
    return () => clearTimeout(id);
  }, [previewInput]);

  const { data: matchCount } = useQuery({
    queryKey: ['saved-search-preview-count', debouncedInput],
    queryFn: () => previewSavedSearchCount(debouncedInput),
    enabled: open && subjectCount > 0 && !budgetRangeInvalid,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: () => {
      // Type narrowing only, not a user-facing error path: `mutate()` below
      // is called from a button that only renders once `search` is set (the
      // sheet has nothing to save without a target), so this never throws in
      // practice — it exists to let TS treat `search` as non-null for the
      // rest of the closure.
      if (!search) throw new Error('no search');
      // Belt-and-suspenders: the Save button is disabled while
      // `budgetRangeInvalid`, but `mutate()` is reachable directly (e.g. a
      // stale click queued before the fields changed), so the mutation itself
      // must refuse too rather than rely solely on the disabled attribute.
      if (budgetRangeInvalid) throw new Error(t('saved.budgetRangeError'));
      // Send `null` to CLEAR a lever the user emptied, rather than omitting the
      // key — omission means "leave alone" on the API, so an emptied field would
      // otherwise silently keep its old value.
      return updateSavedSearch(search.id, {
        name: name.trim() || search.name,
        minBudgetUzs: minBudget.trim() === '' ? null : Number(minBudget),
        maxBudgetUzs: maxBudget.trim() === '' ? null : Number(maxBudget),
        excludeTerms,
        ...(learnedChanged ? { learnedExclusions: learned } : {}),
        // Always sent, even empty — unlike `learnedExclusions` above, an
        // omitted key here would mean "leave alone" and silently keep the OLD
        // list after the user cleared an axis down to zero (legal now that the
        // guard checks the union of all three, not each one alone).
        categorySlugs: categories,
        ikpuCodes,
        nicheSlugs,
        regions,
        kinds,
        textTerms,
        // Always sent — a fully controlled boolean like kinds/regions/
        // textTerms above, not a "changed?" conditional like
        // learnedExclusions. A save with no edits must re-send the seeded
        // value, not silently reset the flag to its column default.
        includeSupply,
        // Always sent (same "present replaces, never omitted" rule as the
        // axes above) — `[]` is how a cleared pin reaches the API, which
        // reads an omitted key as "leave alone", not "clear".
        customerTins: customerTin ? [customerTin] : [],
        customerName: customerName ?? undefined,
      });
    },
    onSuccess: async (updated) => {
      /*
       * Close the ИИ-подбор out by hand.
       *
       * The create endpoint does this itself when it receives an `aiDraftId`,
       * but this sheet PATCHes an existing row — create never sees it — so the
       * outcome has to be reported directly. Fire-and-forget: telemetry must
       * never turn a successful save into an error on screen.
       *
       * The baseline is the form at fill time (see `aiBaseline`), so
       * `editsCount` is what the user changed AFTER the model spoke, which is
       * the number the harness needs.
       */
      if (aiDraftId && aiBaseline) {
        const final = currentAiDraft();
        const edits = countDraftEdits(aiBaseline, final);
        reportOutcomeQuietly(aiDraftId, {
          outcome: edits > 0 ? 'edited_saved' : 'saved',
          savedSearchId: updated?.id ?? search?.id,
          editsCount: edits,
          final: {
            name: final.name,
            categorySlugs: final.categories.map((c) => c.slug),
            ikpuCodes: final.ikpu.map((c) => c.code),
            nicheSlugs: final.niches.map((n) => n.slug),
            regions: final.regions.map((r) => r.code),
            textTerms: final.keywords,
            excludeTerms: final.excludes,
            minBudgetUzs: final.minBudgetUzs,
          },
        });
      }
      await qc.invalidateQueries({ queryKey: ['saved-searches'] });
      onOpenChange(false);
      /*
       * Put the LIST behind the dialog on the criteria that were just saved.
       *
       * The sheet is opened from the sidebar rail, so on `/catalog` the user is
       * editing a subscription with the catalog list visible behind it — and
       * that list keeps whatever filters the URL had when they arrived. On
       * 2026-09-26 the owner added the minus word «ремонт», closed the sheet,
       * and «Текущий ремонт наружного фасада…» was still sitting there, which
       * reads as "the exclusion does not work" rather than "this list is not
       * that query".
       *
       * Navigating to the saved search's own catalog URL is the same thing the
       * sidebar link does — no new concept, and it is a client-side navigation
       * within the page they are already on. Gated on the pathname so an edit
       * from `/app/alerts` or anywhere else still just closes the sheet.
       */
      if (isCatalogListPath(pathname)) navigate(savedSearchToCatalogPath(updated));
    },
  });

  // Names for the slugs, and what is still addable. Loaded only while the sheet
  // is open — a closed sheet must not fetch the whole taxonomy.
  // ⚠️ `locale` is required, in BOTH places. Without it `fetchCategories` sends
  // no `lang` and the API answers in DEFAULT_LOCALE — so a Russian-speaking
  // subscriber edited their subscription against a list of Uzbek category names
  // ("Qishloq xo'jaligi") while the very same categories render in Russian
  // everywhere else in the app. And a queryKey missing the locale would serve
  // one language's names from cache after the user switches to the other.
  //
  // Every other caller (filter-bar, save-search-button, onboarding-modal) had
  // both; this was the lone omission.
  const { data: allCategories } = useQuery({
    queryKey: ['catalog', 'categories', locale],
    queryFn: () => fetchCategories(locale),
    enabled: open,
  });
  const categoryName = (slug: string) => allCategories?.find((c) => c.slug === slug)?.name ?? slug;

  // Same reasoning as the categories query above: `locale` matters (a Russian
  // speaker must not see Uzbek region names), and it is gated on `open` so a
  // closed sheet never fetches the region list.
  // Same key as `SaveSearchButton`'s own regions query (`['catalog', 'regions',
  // locale]`) — TanStack Query dedupes by key, so aligning the two means
  // opening this sheet from that button reuses the already-in-flight/cached
  // request instead of firing a second, identical one.
  const { data: allRegions } = useQuery({
    queryKey: ['catalog', 'regions', locale],
    queryFn: () => fetchRegions(locale),
    enabled: open,
  });

  // Same ИКПУ facet list `SubjectPicker` browses when its dropdown is opened
  // with no query (`['ikpu-facets', locale]` there too) — sharing the key
  // means opening the picker below reuses this same in-flight/cached request.
  // Covers the ~600 codes that have live tenders; a code a saved search
  // subscribes to that has since dropped off the facet list (no live tenders
  // left) falls back to the bare code, same as `categoryName` above.
  const { data: allIkpu } = useQuery({
    queryKey: ['ikpu-facets', locale],
    queryFn: () => fetchIkpuCodes(locale),
    enabled: open,
  });
  // Spain (`features.codeNamesFromTable`): the names of exactly the codes on
  // the chips, off the FULL code table. The facet list above names only the
  // codes with lots enough to browse — 55 % of the codes live lots carry are
  // not in it (CPV check 22.10), and their chips read as bare numbers. Keyed on
  // the sorted set, so adding a chip asks once more and a re-render never.
  const chipCodes = useMemo(() => [...ikpuCodes].sort(), [ikpuCodes]);
  const { data: tableNames } = useQuery({
    queryKey: ['ikpu-names', locale, chipCodes],
    queryFn: () => fetchIkpuNames(chipCodes, locale),
    enabled: open && codeNamesFromTable() && chipCodes.length > 0,
    staleTime: Infinity,
  });
  // The full table first where it is asked (Spain), then the facet list (the
  // localized reference), then whatever the proposal called the code — a code
  // with no live lots today is absent from the facets and would otherwise
  // render as a bare number.
  const ikpuName = (code: string) =>
    tableNames?.[code] ?? allIkpu?.find((i) => i.code === code)?.name ?? aiIkpuNames[code] ?? code;

  // Niche names have no "fetch every niche" facet to browse for a lookup —
  // resolved one at a time via `resolveNiche`, cached in `nicheNames` so a
  // re-render (e.g. toggling a chip) never re-fetches an already-known name.
  useEffect(() => {
    if (!open) return;
    const missing = nicheSlugs.filter((slug) => !resolvedNicheSlugs.current.has(slug));
    if (missing.length === 0) return;
    for (const slug of missing) resolvedNicheSlugs.current.add(slug);
    let cancelled = false;
    Promise.all(missing.map((slug) => resolveNiche(slug, locale))).then((results) => {
      if (cancelled) return;
      setNicheNames((prev) => {
        const next = { ...prev };
        results.forEach((r, i) => {
          next[missing[i]] = r?.name ?? missing[i];
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, nicheSlugs, locale]);
  const nicheName = (slug: string) => nicheNames[slug] ?? slug;

  // The subject axis is ONE union server-side (`compileCriteria`) — category,
  // ИКПУ and niche together, not three independent axes — so the "keep at
  // least one" removal guard below is a single count across all three, not
  // three separate `length > 1` checks. Mirrors the API's own
  // `hasSubjectAfterPatch` guard (`saved-search.repository.ts`).
  /**
   * Collapse a GROUPED category back into one chip.
   *
   * `groupItDigital` folds `it-telecom` + `telecom-equipment` into a synthetic
   * row whose slug is literally `it-telecom,telecom-equipment` and REMOVES both
   * members from the list. The stored `categorySlug` is that joined string, and
   * seeding splits it — so each half is looked up, neither exists, and the rail
   * rendered two raw slugs («it-telecom ✕ telecom-equipment ✕») where one
   * «ИТ и цифровые технологии» chip belongs.
   *
   * Display only: `categories` state keeps the individual member slugs, which
   * is what the PATCH must send (the repository re-joins them, and
   * `criteriaFromLegacy` splits them again to match). Collapsing here and
   * splitting in `toggleSubject` keeps the payload byte-identical.
   */
  const groupedCategorySelections = (): SubjectSelection[] => {
    const remaining = new Set(categories);
    const out: SubjectSelection[] = [];
    for (const c of allCategories ?? []) {
      if (!c.slug.includes(',')) continue;
      const members = c.slug.split(',').filter(Boolean);
      if (members.length > 0 && members.every((m) => remaining.has(m))) {
        members.forEach((m) => remaining.delete(m));
        out.push({ kind: 'category', slug: c.slug, name: c.name });
      }
    }
    // Order follows `categories` so a chip does not jump when another is added.
    for (const slug of categories) {
      if (remaining.has(slug)) out.push({ kind: 'category', slug, name: categoryName(slug) });
    }
    return out;
  };

  const subjectSelected: SubjectSelection[] = [
    ...groupedCategorySelections(),
    ...ikpuCodes.map((code) => ({ kind: 'ikpu' as const, code, name: ikpuName(code) })),
    ...nicheSlugs.map((slug) => ({ kind: 'niche' as const, slug, name: nicheName(slug) })),
  ];
  function toggleSubject(s: SubjectSelection) {
    if (s.kind === 'category') {
      // A grouped chip carries the JOINED slug; its members are what the state
      // holds, so both directions operate on the parts.
      const parts = s.slug.split(',').filter(Boolean);
      if (parts.length > 1) {
        setCategories((p) =>
          parts.every((m) => p.includes(m))
            ? subjectCount > 1
              ? p.filter((m) => !parts.includes(m))
              : p
            : [...new Set([...p, ...parts])],
        );
        return;
      }
      setCategories((p) =>
        p.includes(s.slug)
          ? subjectCount > 1
            ? p.filter((x) => x !== s.slug)
            : p
          : [...p, s.slug],
      );
    } else if (s.kind === 'ikpu') {
      setIkpuCodes((p) =>
        p.includes(s.code)
          ? subjectCount > 1
            ? p.filter((x) => x !== s.code)
            : p
          : [...p, s.code],
      );
    } else {
      setNicheSlugs((p) =>
        p.includes(s.slug)
          ? subjectCount > 1
            ? p.filter((x) => x !== s.slug)
            : p
          : [...p, s.slug],
      );
    }
  }

  /** The ✦ beside a label the model filled in. Decoration plus one sr-only
   *  word: a star character announced on its own is noise, and a field that
   *  reads differently to a screen reader than to everyone else is worse than
   *  no mark at all. */
  const aiMark = (key: string) =>
    aiFields.has(key) ? (
      <>
        <AiSparkle className="ml-1" />
        <span className="sr-only"> {t('aiOnboarding.aiMark')}</span>
      </>
    ) : null;

  /*
   * Keyboard-safe height + lift, the same hook and the same two CSS variables
   * the catalog's filter sheet consumes (catalog-filters.tsx). This sheet never
   * had them: on Igor's Redmi, 17.10 (10a), «Byudjet, so'mdan» sat half under
   * the keyboard. The hook also centres a focused field inside the sheet once
   * the keyboard is up; `BudgetInput` does the same for Chrome on Android,
   * which resizes the layout viewport itself and gets no lift here.
   */
  const sheetRef = useRef<HTMLDivElement>(null);
  useVisualViewportSheetHeight(open, sheetRef);

  const inputClass =
    'h-11 w-full shrink-0 rounded-md border border-edge bg-bone px-3 text-[16px] text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={sheetRef}
        side="bottom"
        /* Bottom sheet on a phone, centred panel from `sm` up.
         *
         * This is the one bottom sheet that is genuinely used on desktop — the
         * catalog's filter sheet only ever opens on mobile — and full-bleed at
         * 1900px stretched a six-field form across the whole viewport, with the
         * Pro block as an empty band and the buttons a metre apart.
         *
         * Centred WITHOUT a transform (`inset-x-0 mx-auto` + a fixed `top`)
         * on purpose: tailwindcss-animate's enter/exit keyframes set `transform`
         * outright, so a `-translate-x-1/2` centring would be dropped for the
         * duration of the animation and the panel would visibly jump. The
         * bottom-slide is also scaled down to a short rise up here — a panel
         * flying the full height of a desktop screen reads as a mobile sheet
         * that escaped. */
        className="mb-[var(--sheet-lift,0px)] max-h-[var(--sheet-max,92vh)] gap-4 rounded-t-2xl border-t border-edge bg-bone-surface p-5
          supports-[height:1dvh]:max-h-[var(--sheet-max,92dvh)]
          sm:inset-0 sm:m-auto sm:h-fit sm:w-[min(560px,92vw)]
          sm:max-h-[86dvh] sm:rounded-2xl sm:border sm:border-edge
          sm:data-[state=open]:slide-in-from-bottom-3
          sm:data-[state=closed]:slide-out-to-bottom-3"
      >
        {/* The ✕ is the only way out now that «Отмена» is gone. Radix closes on
            Esc and on an overlay tap, but neither is discoverable on a phone —
            and 99% of this traffic is a phone. */}
        <div className="flex items-start justify-between gap-3">
          <SheetTitle className="font-brand text-[18px] font-extrabold tracking-[-0.3px] text-ink">
            {t('saved.edit.title')}
          </SheetTitle>
          <button
            type="button"
            aria-label={t('saved.close')}
            onClick={() => onOpenChange(false)}
            className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded text-[15px] leading-none text-ink-muted transition-colors hover:bg-bone hover:text-ink"
          >
            ✕
          </button>
        </div>
        {/* Radix warns without one, and a screen reader otherwise announces the
            sheet by title alone with no hint of what it contains. */}
        <SheetDescription className="sr-only">{t('saved.edit.a11yDesc')}</SheetDescription>

        <div className="flex flex-col gap-3">
          {/* Top of the form on purpose: it is an alternative to filling the
              fields by hand, so it has to be read before the fields, not after
              them. Absent entirely where the feature is off (Uzbekistan). */}
          {aiOn && aiFill && <AiFillBlock onFilled={applyAiProposal} />}
          {aiFields.size > 0 && (
            <p className="-mt-1 flex items-center gap-1.5 text-[12.5px] text-ink-soft">
              <AiSparkle />
              {t('aiOnboarding.fill.filled')}
            </p>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-ink">
              {t('saved.nameLabel')}
              {aiMark('name')}
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
            />
          </label>

          {/* The whole subject axis — category, ИКПУ and niche. Always
              rendered, even with nothing picked yet in one or two of the
              groups: a search that started as keyword-only had no way to add
              its FIRST category either, before this. One shared picker adds
              to whichever kind the user actually clicks; a chip's ✕ is
              disabled exactly when removing it would take `subjectCount` to
              zero — the same "keep at least one across all three" rule the
              API enforces (`hasSubjectAfterPatch`). */}
          <div className="flex flex-col gap-2">
            {/* A label appears here ONLY once a fill has happened — the picker
                is self-describing otherwise, and adding a permanent heading
                would change the manual form for everyone. */}
            {aiFields.has('subject') && (
              <span className="text-[13px] font-semibold text-ink">
                {t('aiOnboarding.row.subject')}
                {aiMark('subject')}
              </span>
            )}
            <SubjectPicker
              mode="multi"
              selected={subjectSelected}
              onSelect={toggleSubject}
              /* Chips render INSIDE the trigger now, scrolling horizontally,
                 rather than as stacked rows above it — five selections used to
                 take four wrapped rows and most of a phone screen. The ✕ is
                 refused when it would empty the subject axis, matching the
                 API's own `hasSubjectAfterPatch` rule. */
              onRemove={toggleSubject}
              canRemove={() => subjectCount > 1}
              triggerLabel={t('saved.categoryAdd')}
              className="mt-1"
            />
          </div>

          {/* Independent of `scope` and of the subject axis above — a search
              of ANY layer can also pick up thin, budgetless `supply`
              (cooperation.uz) leads. Radix `Checkbox`, never a raw native
              checkbox (apps/web/CLAUDE.md's DS-component rule). */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={includeSupply}
              onCheckedChange={(v) => setIncludeSupply(v === true)}
              aria-label={t('saved.includeSupplyLabel')}
            />
            <span className="text-[13px] leading-snug text-ink">
              {t('saved.includeSupplyLabel')}
              <span className="mt-0.5 block text-[12px] text-ink-soft">
                {t('saved.includeSupplyHint')}
              </span>
            </span>
          </label>

          {/* Floor and ceiling — side by side from `sm`, stacked on a phone:
              each now carries a «= 100 mln so'm» line and a row of quick picks,
              which a 165px half-column cannot hold (Igor's Redmi, 17.10). The
              value stays a plain integer string; only the display is grouped. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="saved-edit-min-budget" className="text-[13px] font-semibold text-ink">
                {t('saved.minBudgetLabel')}
                {aiMark('budget')}
              </label>
              <BudgetInput
                id="saved-edit-min-budget"
                value={minBudget}
                onChange={setMinBudget}
                presets={BUDGET_FLOOR_PICKS}
                placeholder={t('saved.minBudgetPlaceholder')}
                className={inputClass}
              />
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="saved-edit-max-budget" className="text-[13px] font-semibold text-ink">
                {t('saved.maxBudgetLabel')}
              </label>
              <BudgetInput
                id="saved-edit-max-budget"
                value={maxBudget}
                onChange={setMaxBudget}
                presets={BUDGET_CEILING_PICKS}
                placeholder={t('saved.maxBudgetPlaceholder')}
                aria-invalid={budgetRangeInvalid}
                className={inputClass}
              />
            </div>
          </div>
          {budgetRangeInvalid && (
            <p className="text-[12px] text-red-600">{t('saved.budgetRangeError')}</p>
          )}

          {/* Every axis is free. The builder used to gate exclude/region/kind/
              free-text behind Pro; that gate is gone deliberately — a precise
              alert is what stops the notifications being noise, and charging for
              precision meant the free tier's own alerts were the worst version
              of the product. The API never gated these axes either
              (`notifications.controller.ts` checks ownership only), so the gate
              was a UI convention rather than an entitlement. */}
          <div>
            <label className="mb-1.5 block text-[13px] font-semibold text-ink">
              {t('saved.excludeTermsLabel')}
              {aiMark('exclude')}
            </label>
            <TagInput
              value={excludeTerms}
              onChange={setExcludeTerms}
              ariaLabel={t('saved.excludeTermsLabel')}
              placeholder={t('saved.excludeTermsPlaceholder')}
              max={SAVED_SEARCH_TERMS_MAX}
            />
            {/* Offered, never applied. An exclusion is the one axis that can
                only ever REMOVE lots, so it does not get to arrive by itself —
                see ai-proposal-draft.ts. */}
            {aiExcludeOffers.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {aiExcludeOffers.map((term) => (
                  <button
                    key={term}
                    type="button"
                    aria-label={t('aiOnboarding.chipAdd', { label: term })}
                    onClick={() => {
                      setExcludeTerms((prev) => (prev.includes(term) ? prev : [...prev, term]));
                      setAiExcludeOffers((prev) => prev.filter((x) => x !== term));
                      setAiFields((prev) => new Set(prev).add('exclude'));
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-edge bg-bone-surface px-3 py-1 text-[12.5px] font-medium text-ink-muted transition-colors hover:border-brand hover:text-brand"
                  >
                    <span aria-hidden="true">+</span>
                    {term}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-1 text-[12px] text-ink-muted">{t('saved.excludeTermsHint')}</p>
          </div>

          <>
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-ink">
                {t('saved.regionLabel')}
                {aiMark('regions')}
              </label>
              <ChipStrip
                scroll
                options={(allRegions ?? []).map((r) => ({
                  value: r.slug,
                  label: regionChipLabel(r.name),
                }))}
                selected={regions}
                onToggle={(v) =>
                  setRegions((prev) =>
                    prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
                  )
                }
              />
              {regions.length === 0 && (
                <p className="mt-1 text-[12px] text-ink-muted">{t('saved.anyRegion')}</p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-ink">
                {t('saved.kindLabel')}
              </label>
              <ChipStrip
                options={PROCUREMENT_KINDS.map((k) => ({
                  value: k,
                  label: t(`catalog.kind.${k}`),
                }))}
                selected={kinds}
                onToggle={(v) =>
                  setKinds((prev) =>
                    prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
                  )
                }
              />
            </div>

            {/* Pinned buyer (заказчик) — a fourth independent axis, ANDed
                against the subject same as region/kind/text. Unlike those
                three it holds at most ONE pin (`SavedSearch.customerTin` is a
                single column, not a criteria array), so it's a type-ahead +
                chip rather than a `ChipStrip`/`TagInput`. */}
            <div>
              <label
                htmlFor="saved-search-customer"
                className="mb-1.5 block text-[13px] font-semibold text-ink"
              >
                {t('saved.customerLabel')}
              </label>
              {customerTin ? (
                <button
                  type="button"
                  onClick={clearCustomer}
                  aria-label={`${t('saved.customerRemove')}: ${customerName ?? customerTin}`}
                  className="inline-flex h-10 items-center gap-1.5 rounded-md border border-brand/40 bg-brand-dim px-3 text-[13px] font-medium text-brand transition-colors hover:bg-brand-dim/70"
                >
                  <span className="max-w-[240px] truncate">{customerName ?? customerTin}</span>
                  <span aria-hidden className="text-brand/70">
                    ✕
                  </span>
                </button>
              ) : (
                <div className="relative" ref={customerFieldRef}>
                  <input
                    id="saved-search-customer"
                    type="text"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder={t('saved.customerPlaceholder')}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={customerDropdownOpen}
                    aria-controls="saved-search-customer-listbox"
                    className={inputClass}
                  />
                  {customerDropdownOpen && customerResults.length > 0 && (
                    <div
                      id="saved-search-customer-listbox"
                      role="listbox"
                      aria-label={t('saved.customerLabel')}
                      className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-edge bg-bone-surface p-1 shadow-m-md"
                    >
                      {customerResults.map((hit) => (
                        <button
                          key={hit.tin}
                          type="button"
                          role="option"
                          aria-selected={false}
                          onClick={() => pickCustomer(hit)}
                          className="flex w-full items-center justify-between gap-2 rounded px-2.5 py-1.5 text-left text-[13px] text-ink transition-colors hover:bg-bone"
                        >
                          <span className="min-w-0 flex-1 truncate">{hit.name}</span>
                          <span className="shrink-0 text-[11px] text-ink-muted">
                            {hit.tenderCount}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="mt-1 text-[12px] text-ink-muted">{t('saved.customerHint')}</p>
            </div>

            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-ink">
                {t('saved.textLabel')}
                {aiMark('text')}
              </label>
              <TagInput
                value={textTerms}
                onChange={setTextTerms}
                ariaLabel={t('saved.textLabel')}
                placeholder={t('saved.textPlaceholder')}
                max={SAVED_SEARCH_TERMS_MAX}
              />
              {/* Spain (`features.codesOverTerms`): with codes the words do not
                  narrow the alert — say so, rather than «any one word is enough». */}
              <p className="mt-1 text-[12px] text-ink-muted">
                {CODES_OVER_TERMS && ikpuCodes.length > 0
                  ? t('saved.textHintCodes')
                  : t('saved.textHint')}
              </p>
            </div>
          </>

          {learned.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-ink">{t('saved.learnedLabel')}</span>
              <div className="flex flex-wrap gap-1.5">
                {learned.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => setLearned((prev) => prev.filter((x) => x !== term))}
                    aria-label={`${t('saved.learnedRemove')}: ${term}`}
                    className="inline-flex h-8 items-center gap-1.5 rounded-full border border-edge bg-bone px-3 text-[13px] text-ink transition-colors hover:border-brand"
                  >
                    <span>{term}</span>
                    <span aria-hidden className="text-ink-muted">
                      ✕
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {save.isError && <p className="text-[12px] text-red-600">{saveErrorText(save.error, t)}</p>}

        {/* One action. «Отмена» is gone: it competed with Save for the eye
            while doing what the ✕ in the header and the overlay already do,
            and on a phone a second full-width button is a mis-tap away from
            the first. Dismissing is the ✕; the destructive act lives in the
            sidebar, next to the list it changes. */}
        {matchCount !== undefined && (
          <p className="text-center text-[12.5px] text-ink-muted">
            {t('saved.matches', { n: matchCount })}
          </p>
        )}
        <button
          type="button"
          disabled={save.isPending || budgetRangeInvalid}
          onClick={() => save.mutate()}
          className="inline-flex h-12 w-full shrink-0 items-center justify-center rounded-md bg-brand text-[14px] font-bold text-white transition-colors hover:bg-brand-mid disabled:opacity-60"
        >
          {save.isPending ? t('saved.edit.saving') : t('saved.save')}
        </button>
      </SheetContent>
    </Sheet>
  );
}
